import { Link } from 'react-router-dom';
import { envPath, keyHead, type Overview } from '../api';
import { LineChart } from '../components/LineChart';
import { quickstart } from '../components/KeyReveal';
import { FeaturesCard } from '../components/FeaturesCard';
import { DisplayAdsCard } from '../components/DisplayAdsCard';
import { displayAdsShown } from '../displayAds';
import { KumoNewsCard } from '../components/KumoNewsCard';
import { AskExamples } from '../components/AskExamples';
import { ago, CodeBlock, Empty, ErrorState, num, PageHeader, Spinner, StatTile } from '../components/ui';
import { useResource } from '../hooks';
import { connectedFrom } from '../homeFlow';
import { useT } from '../i18n';
import { useSession } from '../session';
import { useShell } from './ProjectShell';
import { CLI } from '../cliCommand';
import type { UsageSummary } from '../moneyApi';
import { microsMoney } from './money/UsageParts';
import { CONNECT_PATH } from '../connectPage';
import { OwnerOnly } from '../components/OwnerOnly';

export function OverviewPage() {
  const { project, env } = useShell();
  const r = useResource<Overview>(`${envPath(project.id, env)}/overview`, 30_000);
  const { t, tr } = useT();
  const { developer } = useSession();
  // AI エージェントがつながっているか（ホームと同じ出どころ = 生きている委任トークンがあるか・pages/ProjectsPage.tsx）。
  // メールが未確認なら読まない（403 になる・MCP の接続も確認が要る）= 分からない扱い → つなぐ道を出す
  //   CLI のログイン（cliSignedIn）も数える = ホームと同じ式（homeFlow.ts connectedFrom・2026-11-05 core/first5）
  const delegations = useResource<{ delegations?: unknown[]; cliSignedIn?: boolean }>(developer?.emailVerified === false ? null : '/v1/developers/delegations');
  const { connected } = connectedFrom(delegations.data);
  // 今月の使用額（2026-10-07 console/usage-view・founder「プロジェクトの概要にも今月の使用額と内訳への近道」）。
  // 使用額はアカウント単位の API（GET /v1/money/usage の byProject = 帳票の額をプロジェクトの使った分の比で配った額）から、このプロジェクトの行を引く。
  // 取れない（古い本体 = byProject が無い・お金の API の失敗）ときはタイルを出さない（推測の額を見せない）。行が無い = 今月はまだ使っていない = $0
  const usage = useResource<UsageSummary>('/v1/money/usage');
  const usageMicros = usage.data?.byProject ? (usage.data.byProject.find((p) => p.projectId === project.id)?.costMicros ?? 0) : null;
  const pkPrefix = project.environments.find((e) => e.name === env)?.keys.find((k) => k.kind === 'publishable' && !k.revokedAt)?.prefix;

  return (
    <>
      {/* 見出しはプロジェクトの名前・スラッグは小さく添える（2026-09-30 console/core-first C4）。
          WHY: 以前は見出しが「概要」・その上にスラッグ（booking-test）だけで、名前で作ったプロジェクトがどれか見分けにくかった。
          場所の名前（概要）は上の小さな行に回す。名前が無い・スラッグと同じなら、スラッグを 2 回出さない */}
      <PageHeader eyebrow={t('overview.title')} title={project.name || project.slug} note={project.name && project.name !== project.slug ? project.slug : undefined} />
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('overview.title')} onRetry={r.reload} />}
      {r.data && (
        <>
          {/* 数字は大きい 3 枚（利用者・24 時間の活動・今月の使用額）+ 残りは下の 1 行の小さな字（2026-10-18 design/rauno-dashboard2・
              rauno-audit.md プロジェクトの概要の行）。以前は 6 枚が同じ強さで並び、中の言葉の札（Keys (pk_ / sk_)・Config v4）が利用者の数と同じ大きさだった。
              文・値・リンクは前と同じ。使用額が読めない間（読み込み中・失敗）は「新しい利用者」を大きい 3 枚目にする */}
          {(() => {
            const newTile = { label: t('overview.new24h'), value: num(r.data.players.new24h), hint: t('overview.new7d', { count: r.data.players.new7d }) };
            const third =
              usageMicros !== null
                ? { label: t('overview.usage'), value: microsMoney(usageMicros), hint: <Link to="/money/usage">{t('overview.usageLink')}</Link> }
                : newTile;
            const minor = [
              ...(usageMicros !== null ? [newTile] : []),
              {
                label: t('overview.config'),
                value: r.data.config.version ? `v${r.data.config.version}` : '—',
                hint: r.data.config.pushedAt ? t('overview.pushedAgo', { ago: ago(r.data.config.pushedAt) }) : t('overview.nothingPushed'),
                warn: !r.data.config.version
              },
              {
                label: t('overview.apiKeys'),
                value: num(r.data.keys.active),
                hint: r.data.keys.revoked ? t('overview.revoked', { count: r.data.keys.revoked }) : t('overview.active')
              }
            ];
            return (
              <section aria-label={t('overview.keyNumbers')}>
                <div className="tiles tiles-3">
                  <StatTile
                    label={t('overview.players')}
                    value={num(r.data.players.total)}
                    hint={r.data.players.banned ? t('overview.banned', { count: r.data.players.banned }) : t('overview.allTime')}
                    tone="accent"
                  />
                  <StatTile label={t('overview.active24h')} value={num(r.data.players.active24h)} hint={t('overview.active24hHint')} />
                  <StatTile label={third.label} value={third.value} hint={third.hint} />
                </div>
                <dl className="tiles-minor">
                  {minor.map((m) => (
                    <div key={m.label} className={'warn' in m && m.warn ? 'is-warn' : undefined}>
                      <dt>{m.label}</dt>
                      <dd>
                        <strong>{m.value}</strong> <span>{m.hint}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            );
          })()}

          {/* 次のステップは数字のすぐ下（2026-09-30 リーダーの目視）。WHY: 以前は機能の一覧（21 行）とお知らせの欄の後ろ = 最初の一手が
              ページのいちばん下にあり、新しいプロジェクトを開いた人の目に入らなかった。出るのは利用者 0 人か設定ファイルがまだの間だけ */}
          {(r.data.players.total === 0 || r.data.config.version === 0) && (
            <section className="card next-steps">
              <h2 className="card-title">{t('overview.nextSteps')}</h2>
              {/* 最初の一手 = AI エージェントに頼む（2026-09-30 console/core-first C2・MASTER-PLAN §0「コピペの文をエージェントに貼る → 普段の言葉で頼む」）。
                  以前は「kumo.config.json にステータスとマルチプレイのモードを定義」「HTML に SDK を貼る」「CLI で deploy」の 3 つ = 手で組む前提・ゲーム向けだった。
                  設定ファイルの一手（ステータス・マルチプレイ）は外した: 使うかはプロジェクトしだいで、要るならエージェントが書く */}
              <div className="next-steps-ask">
                <h3 className="next-steps-sub">{t('home.c.askTitle')}</h3>
                <p>{t('overview.ask.body', { name: project.name || project.slug })}</p>
                {/* つないでいない（分からないも含む）ときだけ、コピペの 1 行の画面（/connect）への道。
                    2026-10-07 console/connect-again: 以前はホーム（/）を指していた（ホームの段は未接続のあいだだけ出る）。
                    /connect はいつでも同じ 1 行を出すので、こちらを指す。つないだ人にはここでは出さない（トップバーからいつでも行ける） */}
                {connected !== true && <p>{tr('overview.ask.connect', { link: <Link to={CONNECT_PATH}>{t('overview.ask.connectLink')}</Link> })}</p>}
                <AskExamples />
              </div>
              {/* 自分で打つなら（小さく残す）: SDK の 1 行と CLI の公開。エージェントを使わない人・中身を確かめたい人向け */}
              <div className="next-steps-manual">
                <h3 className="next-steps-sub">{t('overview.byHand')}</h3>
                <ol className="steps">
                  <li className={r.data.players.total ? 'done' : ''}>
                    <strong>{t('overview.step2Title')}</strong>
                    <span className="step-gap" />
                    {t('overview.step2Body')}
                    <CodeBlock code={quickstart(pkPrefix ? keyHead(pkPrefix) : 'pk_…')} label="index.html" />
                  </li>
                  <li>
                    <strong>{t('overview.step3Title')}</strong>
                    <span className="step-gap" />
                    {tr('overview.step3Body', { cmd: <code>{env === 'production' ? `${CLI} deploy ./dist` : `${CLI} deploy ./dist --env development`}</code>, link: <Link to="../hosting">{t('overview.step3Link')}</Link> })}
                  </li>
                </ol>
              </div>
            </section>
          )}

          <section className="card">
            <div className="card-head">
              <h2 className="card-title">{t('overview.dauTitle')}</h2>
              <span className="muted small">{t('overview.dauRange')}</span>
            </div>
            {r.data.players.total === 0 ? (
              <Empty title={t('overview.noPlayersTitle')}>
                <p>{t('overview.noPlayersBody')}</p>
              </Empty>
            ) : (
              <LineChart data={r.data.dailyActive.map((d) => ({ date: d.date, value: d.players }))} label={t('overview.dauLabel')} />
            )}
          </section>

          {/* 機能の ON/OFF（既定はすべて OFF。画面から切り替えは確認つき。2026-09-27 task/toggles・dashboard-review） */}
          <FeaturesCard projectId={project.id} slug={project.slug} env={env} />

          {/* バナー広告（Adsterra の共通の枠を公開中のページに入れる・2026-10-19 creator/ads-open）。広告を隠している間は出さない */}
          {/* 広告（お金）は持ち主だけ（2026-10-22 core/handoff-1b） */}
          {displayAdsShown() && (
            <OwnerOnly>
              <DisplayAdsCard projectId={project.id} slug={project.slug} env={env} />
            </OwnerOnly>
          )}

          {/* Kumo からのお知らせの欄（既定で出す・ここで外せる。founder 2026-09-27 §1c・2026-09-27 news-optout） */}
          <KumoNewsCard projectId={project.id} slug={project.slug} env={env} />

        </>
      )}
    </>
  );
}
